<svelte:options customElement={{
    tag: 'account-update-wizard',
    shadow: 'none',
    props: {
      validationErrors: { reflect: true, attribute: "validation-errors" },
      step: { reflect: true, type: 'Number' },
      action: { reflect: true, attribute: "asp-action" },
      currentEmail: { reflect: true, attribute: "current-email" },
      currentEmailIsConfirmed: { reflect: true, attribute: "current-email-is-confirmed" },
      invitationCode: { reflect: true, attribute: "invitation-code" },
      flow: { reflect: true, type: 'Number', attribute: "flow" },
      verificationCode: { reflect: true, attribute: "verification-code" },
      passwordRepetiton: { reflect: true, attribute: "password-repetition" },
      isPayrollManagedAccount: { reflect: false, attribute: "is-payroll-managed-account" },
      
      antiforgeryToken: { reflect: false, attribute: "antiforgery-token" },
      formAction: { reflect: false, attribute: "form-action" },
      showPanelOnly: { reflect: false, attribute: "show-panel-only" },
    },
    extend: FormAssociatedMixin,
 }}
/>

<script lang="ts">
  import { FormAssociatedMixin } from './form-associated-mixin';
  import { createEventDispatcher, type EventDispatcher, onMount } from "svelte";
  
  import Header from '../wizard-header.svelte';
  import StartPage from './wizard-page-start.svelte';
  import DataPage from './wizard-page-data.svelte';
  import VerificationPage from './wizard-page-verification.svelte';
  import PasswordPage from './wizard-page-password.svelte';
  import SummaryPage from './wizard-page-summary.svelte';
  import ActionButton from "../action-button.svelte";
  
  import { WizardApi } from './wizard-api';
  import { Translations } from "../translations";
  import { Notifications } from "../notifications";
  import { WizardPages } from './constants';

  export let attachedInternals: any = {};
  export let validationErrors: string = "{}";
  export let step: number = 0;
  export let steps: Array<string>;
  export let invitationCode: string = "";
  export let flow: number = 4;
  export let email: string = "";
  export let currentEmail: string = "";
  export let currentEmailIsConfirmed: boolean = false;
  export let firstname: string = "";
  export let lastname: string = "";
  export let verificationCode: string = "";
  export let password: string = "";
  export let passwordRepetition: string = "";
  export let isPayrollManagedAccount: boolean = false;

  export let showPanelOnly: boolean = false;
  export let antiforgeryToken: string = "";

  const Flow_Legacy: number = 1;
  const Flow_Invitation: number = 4;
  const Flow_Planned: number = 32;

  let translations: Translations = new Translations(((window as any).localization) || {});
  let api: WizardApi = new WizardApi();
  let isWorking: boolean = false;
  let formData: FormData = new FormData();
  let allowExistingAccount: boolean = false;
  let form: any;
  let dispatch: any = createEventDispatcher();

  $: errors = JSON.parse(validationErrors)|| {};
  $: goBackEnabled = (flow === Flow_Invitation) ? step > 1 : step > 2;
  
  onMount(() => {
    steps = (flow === Flow_Invitation) 
      ? [translations.get("Start"), translations.get("Data"), translations.get("Verification"), translations.get("Password"), translations.get("Ready")]
      : [translations.get("Data"), translations.get("Verification"), translations.get("Password"), translations.get("Ready")];
    
    formData = new FormData();
    formData.set("InvitationCode", invitationCode);
    formData.set("ConfigurationFlow", flow.toString());
    formData.set("Email", email);
    formData.set("Firstname", firstname);
    formData.set("Lastname", lastname);
    formData.set("VerificationCode", verificationCode);
    formData.set("Password", password);
    formData.set("PasswordRepetition", passwordRepetition);
    formData.set("ConfigurationState", step.toString());

    formData.set("__RequestVerificationToken", antiforgeryToken);

    attachedInternals.setFormValue(formData);
  })
  
  function onCancelClicked(event: MouseEvent): void {
    const shouldContinue = dispatch('cancel', '', { cancelable: true })
    
    if (shouldContinue) {
      formData.delete("Tenant");
      formData.delete("ReturnUrl");
      formData.delete("__RequestVerificationToken");

      formData.set("button", "cancel");

      attachedInternals.setFormValue(formData);
      attachedInternals.form.submit();
    }
  }
  
  async function onNextClicked(event: any): Promise<void> {
    isWorking = true;
    
    try {
      switch(step) {
        case WizardPages.Start: {
          step = flow === Flow_Invitation ? WizardPages.Invitation : WizardPages.PersonalInformation;
          break;
        }
        case WizardPages.Invitation: {
          await handleInvitationPageDataAsync(event);
          break;
        }
        case WizardPages.PersonalInformation: {
          if (allowExistingAccount) {
            await handleLinkCredentialsPageInput(event);
          } else {
            await handlePersonalInformationPageDataAsync(event);
          }
          break;
        }
        case WizardPages.Verification: {
          await handleVerificationPageDataAsync(event);  
          break;
        }
        case WizardPages.Password: {
          await handlePasswordPageDataAsync(event);  
          break;
        }
        case WizardPages.Summary: {
          await handleSummaryPageDataAsync(event);
          break;
        }
        case WizardPages.Finish: {
          await finishFormAsync(event);
          break;
        }
      }
    } catch (e) {
      new Notifications().showError(e as string, translations.get("Initialize your account"));
    } finally {
      formData.set("ConfigurationState", step.toString());
      dispatch('stepChanged', step);
      isWorking = false;

      if (errors[""]) {
        new Notifications().showError(errors[""].join('\r\n'), translations.get("Initialize your account"));
      }
    }
  }
  
  async function onSendCodeClicked(event: any): Promise<void> {
    const result = await api.sendVerificationCodeAsync(formData);
    
    if (!result.success) {
      new Notifications().showError(result.message, translations.get("Initialize your account"));
    } else {
      new Notifications().showInformation(translations.get("Verification Code sent"), translations.get("Initialize your account"), true);
    }
  }

  async function onGoBackClicked(event: any): Promise<void> {
    if (step > 0) {
      step--;
      formData.set("ConfigurationState", step.toString());
      dispatch('stepChanged', step);
    }
  }

  //#region form handlers
  async function handleInvitationPageDataAsync(event: any): Promise<void> {
    formData.set("InvitationCode", invitationCode);
          
    let result = await api.validateAsync(formData);
    if (result.success) {
      isPayrollManagedAccount = (result.data as any).isPayrollManagedAccount;
      firstname = (result.data as any).firstName;
      lastname = (result.data as any).lastName;
      email = (result.data as any).email;
      step = WizardPages.PersonalInformation;
    } else {
      errors = result.errors;
    }
  }

  async function handlePersonalInformationPageDataAsync(event: any): Promise<void> {
    formData.set("Firstname", firstname);
    formData.set("Lastname", lastname);
    formData.set("Email", email);

    if (flow === Flow_Planned && email === currentEmail && currentEmailIsConfirmed) {
      step = WizardPages.Summary;
    } else {
      let result = await api.validateAsync(formData);
      if (result.success) {
        step = WizardPages.Verification;
      } else if (!result.success && (result.errors["error-id"] && result.errors["error-id"].includes("email-already-exists"))) {
        allowExistingAccount = true;
        errors = result.errors;
      } else {
        errors = result.errors;
      }
    }
  }

  async function handleLinkCredentialsPageInput(event: any): Promise<void> {
    formData.set("Password", password);
    
    var response = await api.linkAccountAsync(formData);
    if (response.success) {
      step = WizardPages.Finish;
    } else {
      response.errors.Email = errors.Email;
      errors = response.errors;
    }
  }


  async function handleVerificationPageDataAsync(event: any): Promise<void> {
    formData.set("VerificationCode", verificationCode);
          
    let result = await api.validateAsync(formData);
    if (result.success) {
      step = WizardPages.Password;
    } else {
      errors = result.errors;
    }
  }

  async function handlePasswordPageDataAsync(event: any): Promise<void> {
    formData.set("Password", password);
    formData.set("PasswordRepetition", passwordRepetition);
    
    let result = await api.validateAsync(formData);
    if (result.success) {
      step = WizardPages.Summary;
    } else {
      errors = result.errors;
    }
  }

  async function handleSummaryPageDataAsync(event: any): Promise<void> {
    let result = await api.sendAsync(formData);
    if (result.success) {
      step = WizardPages.Finish;
    } else {
      errors = result.errors;
    }
  }
  
  async function finishFormAsync(event: any): Promise<void> {
    formData.delete("InvitationCode");
    formData.delete("Password");
    formData.delete("PasswordRepetition");
    formData.delete("__RequestVerificationToken");

    attachedInternals.setFormValue(formData);
    attachedInternals.form.submit();
  }
  //#endregion
</script>

<style lang="scss">
  .wk-form-buttons {
    background: #f6f6f6;
    padding: 1rem 1rem;
    position: relative;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 1rem -2rem -2rem;
  }

  @media only screen and (max-width: 480px) {
    .wk-form-buttons {
      margin: 1rem -1rem -1rem;
    }
  }
  
  .wk-button-no-right-margin {
    margin-right: 0 !important;
  }

  .wk-custom-modal-header {
    display: flex;
    flex-direction: row;
    padding-bottom: 1rem;
    .wk-modal-title {
      flex: 1;

      color: #353535;
      font-size: 1.25rem;
      font-weight: 500;
      line-height: 1.3;
      margin: 0;
    }
  }
  .wk-padding-top-2 {
    padding-top: 2rem;
  }
  .wk-padding-top-1 {
    padding-top: 1rem;
  }
  .wk-fixed-width {
    width: 5rem;
  }
  .image-container {
    background-color: #f2f8fc;
    text-align: center;
    padding: 1rem;

    img {
      height: 6rem;
    }
  }
  .summary-description {
    width: 50%;
  }

  .hidden-input-fields {
    opacity: 0;
    position: absolute;
  }
</style>



{#if showPanelOnly}
  <div class="wk-custom-modal-header">
    <div class="wk-modal-title">{translations.get("Your ADDISON user account")}</div>
    {#if step > 0 && step < 6}
      <Header currentStep={flow == Flow_Invitation ? step-1 : step-2} {steps} isCompact={true} />
    {/if}
  </div>  
{/if}
{#if step === WizardPages.Start}
<div class="image-container">
  <img alt="" src="../images/benutzerkonto.svg" />
</div>
<div class="wk-grid wk-padding-top-1">
  <div class="wk-field-helper">{translations.get("We want to make sure that your data is up to date. Please set up your ADDISON user account now.")}</div>
</div>
{:else if step === WizardPages.Invitation}
<div class="wk-column-12 wk-idp-wizard-headline-margin">
  <div class="wk-field-helper">{translations.get("You have received an invitation code to initialize your user account for the ADDISON portal.")}</div>
</div>
<StartPage {translations} {errors} bind:invitationCode={invitationCode} />
{:else if step === WizardPages.PersonalInformation}
<div class="wk-column-12 wk-idp-wizard-headline-margin">
  <div class="wk-field-helper">{translations.get("With the ADDISON user account, you can simply log in anywhere with your e-mail address.")}</div>
</div>
<DataPage {translations} {errors} bind:firstname={firstname} bind:lastname={lastname} bind:email={email} bind:password={password} {isPayrollManagedAccount} bind:allowExistingAccount />
{:else if step === WizardPages.Verification}
<div class="wk-column-12 wk-idp-wizard-headline-margin">
  <div class="wk-field-helper">{translations.getWithParams("For verification purposes, we have sent a security code to your e-mail address {0}. Please enter it here.", [email])}</div>
</div>
<VerificationPage {translations} {errors} bind:verificationCode={verificationCode} on:sendCodeClick={onSendCodeClicked} />
{:else if step === WizardPages.Password}
<div class="wk-column-12 wk-idp-wizard-headline-margin">
  <div class="wk-field-helper">{@html translations.getWithParams("Please enter a <b>new password</b> to log in via email to {0}.", [email])}</div>
</div>
<PasswordPage {translations} {errors} bind:password={password} bind:passwordRepetition={passwordRepetition} />
{:else if step === WizardPages.Summary}
<SummaryPage {translations} {flow} {email} {password} />
{:else if step === WizardPages.Finish}
  <input autocomplete="username" type='text' name='username' class="hidden-input-fields" required bind:value={email} inert />
  <input autocomplete="new-password" type='password' name='password' class="hidden-input-fields" required bind:value={password} inert/>


  <div class="wk-grid">
    <p>{@html translations.get("Your ADDISON user account has been set up successfully. You can now log in.")}</p>
  </div>
  <img class="summary-description" src="../images/login_profileswitch_description_mobile.png" alt="" />
{/if}
<div class="wk-form-buttons wk-button-group wk-button-group-right">
  {#if (step == 0)} 
    <button type="button" name="button" value="cancel" class="wk-button wk-button-text wk-button-small" on:click|preventDefault|stopPropagation={onCancelClicked}>{translations.get("Cancel")}</button>
    <ActionButton type="submit" name="button" value="invite" active="{isWorking}" title={translations.get("Set up user account")} on:click={onNextClicked}></ActionButton>
  {/if}
  {#if (step > 0 && step < 5)}
    <button type="button" name="button" value="cancel" class="wk-button wk-button-text wk-button-small" on:click|preventDefault|stopPropagation={onCancelClicked}>{translations.get("Cancel")}</button>
    <button type="button" name="button" value="back" class="wk-button wk-button-ghost wk-button-small wk-button-no-right-margin" disabled={!goBackEnabled} on:click|preventDefault|stopPropagation={onGoBackClicked}>{translations.get("Back")}</button>
    <ActionButton type="submit" name="button" value="invite" active="{isWorking}" title={translations.get("Next")} on:click={onNextClicked}></ActionButton>
  {/if}
  {#if (step == 5)}
    <ActionButton type="submit" name="button" value="invite" active="{isWorking}" title={translations.get("Finalise setup")} on:click={onNextClicked}></ActionButton>
  {/if}
  {#if (step == 6)}
    <ActionButton type="submit" name="button" value="invite" active="{isWorking}" title={translations.get("Understood")} on:click={onNextClicked}></ActionButton>
  {/if}
</div>
